import { Checkbox } from "@chakra-ui/react";
import Modal from "newui/components/ui/Modal/Modal";
import { ScorePreviewTable } from "./ScorePreviewTable";
import type { SupplierScorePreview, CriterionScorePreview } from "../types/scoring.type";
import { ModalSize } from "newui/components/ui/Modal";
import { IconBox } from "newui/components/IconBox/IconBox";
import { WarningBox } from "newui/components/IconBox";

interface SubmitEvaluationConfirmDialogProps {
	confirmChecked: boolean;
	criteria: CriterionScorePreview[];
	isOpen: boolean;
	isSubmitting?: boolean;
	mode: "initial" | "final";
	suppliers: SupplierScorePreview[];
	onCancel: () => void;
	onConfirm: () => void;
	onConfirmCheckedChange: (checked: boolean) => void;
}

const getModalSize = (supplierCount: number): ModalSize => {
	if (supplierCount >= 5) return "xxxl";
	if (supplierCount >= 3) return "xxl";
	if (supplierCount >= 2) return "xl";
	return "md";
};

export const SubmitEvaluationConfirmDialog = ({
	isOpen,
	mode,
	suppliers,
	criteria,
	confirmChecked,
	onConfirmCheckedChange,
	isSubmitting = false,
	onConfirm,
	onCancel,
}: SubmitEvaluationConfirmDialogProps) => {
	return (
		<Modal
			isOpen={isOpen}
			onClose={onCancel}
			title="Submit Evaluations"
			size={getModalSize(suppliers.length)}
			iconType="warning"
			modalType="standard"
			closeOnEsc
			closeOnOverlayClick={false}
			primaryButton={{
				text: isSubmitting ? "Submitting..." : "Submit",
				onClick: onConfirm,
				disabled: !confirmChecked || isSubmitting,
				width: "full"
			}}
			secondaryButton={{
				text: "Cancel",
				onClick: onCancel,
				disabled: isSubmitting,
				width: "full"
			}}
		>
			<div className="space-y-4">
				<WarningBox>
					<p>
						Once submitted, your evaluation scores and comments cannot be edited.
					</p>
					<p>Please ensure all scores are accurate before proceeding.</p>
				</WarningBox>

				<ScorePreviewTable
					mode={mode}
					suppliers={suppliers}
					criteria={criteria}
				/>

				<label
					className="flex items-start gap-3 cursor-pointer rounded-lg border border-gray-200 p-3 hover:bg-blue-50 transition-colors"
					data-testid="submit-confirm-checkbox"
				>
					<Checkbox
						isChecked={confirmChecked}
						onChange={(e) => onConfirmCheckedChange(e.target.checked)}
						colorScheme="blue"
						mt={0.5}
					/>
					<span className="text-sm text-gray-700">
						I confirm that I have completed all evaluations to the best of my knowledge
						and understand that scores cannot be changed after submission.
					</span>
				</label>
			</div>
		</Modal>
	);
};
